/**
 * PATH: apps/ai/src/pages/dashboard/EvidencePanel.tsx
 * PURPOSE: Right panel — Chain of Thought, citations, privacy toggle, model info
 */

import {
  Brain, BookOpen, Lock, Globe, Cpu, Clock, Database,
  Mail, FileText, Receipt, Timer, FileSearch,
  ChevronDown, Lightbulb, Info, Search,
  PanelRightClose, ExternalLink, Download, File,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { useLocalStorage } from './utils';
import type { Citation } from './types';
import { getAccessToken, getTenantId } from '../../services/streaming/auth';
import { API_BASE } from '@quotech/api';

const DASHBOARD_BASE = 'https://dashboard.quotech.ai';
const QUOTE_BASE = 'https://quote.quotech.ai';

const sourceTypeConfig: Record<string, { icon: typeof BookOpen; label: string; color: string }> = {
  product_docs: { icon: BookOpen, label: 'Product Knowledge', color: 'from-blue-500 to-indigo-600' },
  customer_email: { icon: Mail, label: 'Customer Email', color: 'from-amber-500 to-orange-600' },
  customer_file: { icon: FileText, label: 'Customer File', color: 'from-green-500 to-emerald-600' },
  job_data: { icon: Database, label: 'Job Data', color: 'from-purple-500 to-violet-600' },
  quote_data: { icon: Receipt, label: 'Quote Data', color: 'from-pink-500 to-rose-600' },
  invoice_data: { icon: Receipt, label: 'Invoice Data', color: 'from-emerald-500 to-teal-600' },
  timesheet_data: { icon: Timer, label: 'Timesheet Data', color: 'from-cyan-500 to-blue-600' },
  document: { icon: File, label: 'Document', color: 'from-slate-500 to-gray-600' },
  file: { icon: File, label: 'File', color: 'from-slate-500 to-gray-600' },
  client: { icon: BookOpen, label: 'Client Data', color: 'from-indigo-500 to-blue-600' },
  invoice: { icon: Receipt, label: 'Invoice', color: 'from-emerald-500 to-teal-600' },
  timesheet: { icon: Timer, label: 'Timesheet', color: 'from-cyan-500 to-blue-600' },
};

function getCitationUrl(citation: Citation): string | null {
  if (citation.sourceUrl) return citation.sourceUrl;
  const st = citation.sourceType?.toLowerCase() || '';
  const id = citation.sourceId || citation.jobId;
  if (st.includes('job') && id) return `${DASHBOARD_BASE}/jobs/${id}`;
  if (st.includes('quote') && id) return `${QUOTE_BASE}/builder/${id}`;
  if (st.includes('invoice') && id) return `${DASHBOARD_BASE}/jobs/${id}`;
  if (st.includes('timesheet') && id) return `${DASHBOARD_BASE}/jobs/${id}`;
  if (st.includes('client') && id) return `${DASHBOARD_BASE}/jobs`;
  if ((st.includes('document') || st.includes('file')) && citation.sourceUrl) return citation.sourceUrl;
  return null;
}

async function handleFileDownload(url: string) {
  const token = getAccessToken();
  if (!token) { window.open(url, '_blank'); return; }

  const tenantId = getTenantId();
  const apiUrl = url.startsWith('http') ? url : `${API_BASE}${url}`;

  try {
    const res = await fetch(apiUrl, {
      headers: {
        Authorization: `Bearer ${token}`,
        'X-Organisation-ID': tenantId,
      },
    });
    if (!res.ok) {
      alert('Download failed — file may not exist or access is denied.');
      return;
    }
    const data = await res.json();
    if (data.url) {
      window.open(data.url, '_blank');
    } else {
      alert('Original file is not available for download. Try re-processing via Context Builder.');
    }
  } catch {
    alert('Download failed — network error.');
  }
}

function handleCitationClick(citation: Citation) {
  const url = getCitationUrl(citation);
  if (!url) return;
  if (url.includes('/api/qdrive/') || url.includes('/api/files/')) {
    handleFileDownload(url);
  } else {
    window.open(url, '_blank');
  }
}

interface EvidencePanelProps {
  citations?: Citation[];
  chainOfThought?: string;
  model?: string;
  latencyMs?: number;
  isLoading: boolean;
  onCollapse: () => void;
  privacy: 'private' | 'shared';
  onPrivacyChange: (privacy: 'private' | 'shared') => void;
}

export function EvidencePanel({
  citations = [], chainOfThought, model, latencyMs, isLoading,
  onCollapse, privacy, onPrivacyChange,
}: EvidencePanelProps) {
  const [cotExpanded, setCotExpanded] = useLocalStorage('ai-hub-cot-expanded', true);
  const [citationsExpanded, setCitationsExpanded] = useLocalStorage('ai-hub-citations-expanded', true);

  const groupedCitations = citations.reduce((acc, c) => {
    const type = c.sourceType || 'job_data';
    if (!acc[type]) acc[type] = [];
    acc[type].push(c);
    return acc;
  }, {} as Record<string, Citation[]>);

  const hasContent = chainOfThought || citations.length > 0;

  return (
    <div className="h-full flex flex-col overflow-hidden bg-card rounded-xl border border-border">
      {/* Header */}
      <div className="flex-shrink-0 px-4 py-3 border-b border-border">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="p-1.5 rounded-lg bg-gradient-to-br from-primary to-primary/80 shadow-sm"><BookOpen className="w-4 h-4 text-primary-foreground" /></div>
            <div><h3 className="text-sm font-bold text-foreground">Evidence & Reasoning</h3><p className="text-[10px] text-muted-foreground">Sources & thought process</p></div>
          </div>
          <button onClick={onCollapse} className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-accent transition-colors" title="Collapse panel"><PanelRightClose className="w-4 h-4" /></button>
        </div>
      </div>

      {/* Privacy Toggle */}
      <div className="flex-shrink-0 px-4 py-2.5 border-b border-border">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {privacy === 'private' ? <Lock className="w-3.5 h-3.5 text-muted-foreground" /> : <Globe className="w-3.5 h-3.5 text-primary" />}
            <span className="text-[11px] font-medium text-foreground">{privacy === 'private' ? 'Private' : 'Shared with org'}</span>
          </div>
          <button onClick={() => onPrivacyChange(privacy === 'private' ? 'shared' : 'private')} className={cn("relative w-10 h-5 rounded-full transition-colors", privacy === 'shared' ? "bg-primary" : "bg-accent border border-border")}>
            <span className={cn("absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform", privacy === 'shared' ? "translate-x-5" : "translate-x-0.5")} />
          </button>
        </div>
        <p className="text-[9px] text-muted-foreground mt-1">{privacy === 'private' ? 'Only you can see this conversation' : 'Visible to your organization'}</p>
      </div>

      {/* Meta info */}
      {(model || (latencyMs !== undefined && latencyMs > 0)) && (
        <div className="flex-shrink-0 px-4 py-2 border-b border-border flex flex-wrap gap-2">
          {model && <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] bg-accent text-muted-foreground rounded-md border border-border"><Cpu className="w-3 h-3" /> QuotechAI</span>}
          {latencyMs !== undefined && latencyMs > 0 && <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] bg-accent text-muted-foreground rounded-md border border-border"><Clock className="w-3 h-3" /> {latencyMs}ms</span>}
          {citations.length > 0 && <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] bg-primary/10 text-primary rounded-md border border-primary/20"><Database className="w-3 h-3" /> {citations.length} sources</span>}
        </div>
      )}

      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {/* Chain of Thought */}
        {(chainOfThought || isLoading) && (
          <div className="rounded-xl border border-border bg-gradient-to-br from-accent/50 to-accent/30 overflow-hidden">
            <button onClick={() => setCotExpanded(!cotExpanded)} className="w-full flex items-center justify-between gap-2 p-3 hover:bg-accent/50 transition-colors">
              <div className="flex items-center gap-2">
                <div className="p-1 rounded-md bg-primary/10"><Brain className="w-3.5 h-3.5 text-primary" /></div>
                <span className="text-[11px] font-bold text-foreground uppercase tracking-wider">Reasoning Process</span>
                {isLoading && <span className="flex gap-1"><span className="w-1 h-1 rounded-full bg-primary animate-pulse" /><span className="w-1 h-1 rounded-full bg-primary animate-pulse" style={{ animationDelay: "150ms" }} /><span className="w-1 h-1 rounded-full bg-primary animate-pulse" style={{ animationDelay: "300ms" }} /></span>}
              </div>
              <ChevronDown className={cn("w-4 h-4 text-muted-foreground transition-transform", cotExpanded && "rotate-180")} />
            </button>
            {cotExpanded && (
              <div className="px-3 pb-3">
                <div className="text-[11px] text-muted-foreground leading-relaxed font-mono whitespace-pre-wrap max-h-48 overflow-y-auto rounded-lg p-3 bg-background border border-border shadow-inner">
                  {chainOfThought || (isLoading ? <span className="flex items-center gap-2 text-primary animate-pulse">Analyzing your request and gathering context...</span> : "")}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Citations */}
        {citations.length > 0 && (
          <div className="rounded-xl border border-border overflow-hidden">
            <button onClick={() => setCitationsExpanded(!citationsExpanded)} className="w-full flex items-center justify-between gap-2 p-3 bg-accent/20 hover:bg-accent/40 transition-colors">
              <div className="flex items-center gap-2">
                <div className="p-1 rounded-md bg-emerald-500/10"><FileSearch className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400" /></div>
                <span className="text-[11px] font-bold text-foreground uppercase tracking-wider">Sources Referenced</span>
                <span className="px-1.5 py-0.5 text-[9px] bg-emerald-100 dark:bg-emerald-900/50 text-emerald-700 dark:text-emerald-400 rounded-full font-bold">{citations.length}</span>
              </div>
              <ChevronDown className={cn("w-4 h-4 text-muted-foreground transition-transform", citationsExpanded && "rotate-180")} />
            </button>
            {citationsExpanded && (
              <div className="p-3 space-y-3 bg-background">
                {Object.entries(groupedCitations).map(([sourceType, items]) => {
                  const config = sourceTypeConfig[sourceType] || sourceTypeConfig.job_data;
                  const Icon = config.icon;
                  return (
                    <div key={sourceType} className="space-y-2">
                      <div className="flex items-center gap-2">
                        <div className={cn("p-1 rounded-md bg-gradient-to-br text-white shadow-sm", config.color)}><Icon className="w-2.5 h-2.5" /></div>
                        <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">{config.label}</span>
                        <span className="text-[9px] text-muted-foreground">({items.length})</span>
                      </div>
                      {items.map((citation, idx) => {
                        const citUrl = getCitationUrl(citation);
                        const isDownload = citation.sourceType?.includes('document') || citation.sourceType?.includes('file');
                        return (
                          <div 
                            key={idx} 
                            onClick={() => citUrl && handleCitationClick(citation)}
                            className={cn(
                              "ml-5 p-2.5 rounded-lg bg-accent/30 border border-border transition-all group",
                              citUrl ? "hover:border-primary/30 hover:bg-accent/50 cursor-pointer" : "cursor-default"
                            )}
                          >
                            <div className="flex items-start justify-between gap-2">
                              <div className="flex-1 min-w-0">
                                <span className="text-[11px] font-medium text-foreground truncate block group-hover:text-primary transition-colors">{citation.sourceName}</span>
                                {citUrl && (
                                  <span className="text-[9px] text-muted-foreground">{isDownload ? 'Click to download' : 'Click to open'}</span>
                                )}
                              </div>
                              {citUrl && (
                                isDownload 
                                  ? <Download className="w-3 h-3 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
                                  : <ExternalLink className="w-3 h-3 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
                              )}
                            </div>
                            {citation.relevance != null && citation.relevance > 0 && (
                              <div className="mt-2 flex items-center gap-2">
                                <div className="flex-1 h-1 rounded-full bg-border overflow-hidden">
                                  <div className={cn("h-full rounded-full transition-all", citation.relevance >= 0.7 ? "bg-emerald-500" : citation.relevance >= 0.4 ? "bg-amber-500" : "bg-muted-foreground")} style={{ width: `${Math.max(10, Math.round(citation.relevance * 100))}%` }} />
                                </div>
                                <span className={cn("text-[9px] font-medium", citation.relevance >= 0.7 ? "text-emerald-600 dark:text-emerald-400" : citation.relevance >= 0.4 ? "text-amber-600 dark:text-amber-400" : "text-muted-foreground")}>{Math.round(citation.relevance * 100)}%</span>
                              </div>
                            )}
                            {citation.snippet && <p className="text-[10px] text-muted-foreground mt-2 line-clamp-2 italic">"{citation.snippet}"</p>}
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Empty State */}
        {!isLoading && !hasContent && (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <div className="relative mb-4">
              <div className="w-16 h-16 rounded-2xl bg-accent flex items-center justify-center"><Info className="w-8 h-8 text-muted-foreground" /></div>
              <div className="absolute -bottom-1 -right-1 w-6 h-6 rounded-lg bg-primary/10 flex items-center justify-center border border-primary/20"><Search className="w-3 h-3 text-primary" /></div>
            </div>
            <p className="text-sm font-semibold text-foreground mb-1">No sources yet</p>
            <p className="text-xs text-muted-foreground max-w-[200px]">Ask a question to see the AI's reasoning and retrieved evidence</p>
          </div>
        )}

        {/* Pro tip */}
        {hasContent && (
          <div className="mt-4 p-3 rounded-xl bg-primary/5 border border-primary/10">
            <div className="flex items-start gap-2">
              <Lightbulb className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
              <div><p className="text-[10px] font-medium text-primary">Traceability</p><p className="text-[9px] text-muted-foreground">Every response is linked to its source data for full audit trails.</p></div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
